const entityKeys = (entity) => [entity?.documentId, entity?.id]
    .filter((value) => value !== null && value !== undefined)
    .map(String);

export function sameWorkspaceMembership(left, right) {
    const rightKeys = new Set(entityKeys(right));
    return entityKeys(left).some((key) => rightKeys.has(key));
}

export function normalizeProjectMemberSelections(items) {
    return (Array.isArray(items) ? items : [])
        .map((item) => {
            if (item?.documentId || item?.id) return item;
            return item?.workspaceMembership || item?.member || null;
        })
        .filter((membership) => membership?.documentId || membership?.id);
}

function roleItems(value) {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.data)) return value.data;
    return [];
}

function roleIdentity(role) {
    if (role?.scope_type) return `${role.scope_type}:${role?.scope_id || ""}`;
    return role?.documentId || role?.id || role?.role || "";
}

export function mergeProjectMemberRoleSources(projectMemberships, workspaceMemberships) {
    const workspaceItems = Array.isArray(workspaceMemberships) ? workspaceMemberships : [];
    return (Array.isArray(projectMemberships) ? projectMemberships : []).map((projectMembership) => {
        const workspaceMembership = workspaceItems.find((candidate) => (
            sameWorkspaceMembership(candidate, projectMembership)
        ));
        if (!workspaceMembership) return projectMembership;

        const roles = [...roleItems(workspaceMembership.user_roles), ...roleItems(projectMembership.user_roles)]
            .reduce((items, role) => {
                const key = String(roleIdentity(role));
                const index = items.findIndex((item) => String(roleIdentity(item)) === key);
                if (index === -1) return [...items, role];
                const next = [...items];
                next[index] = {...next[index], ...role};
                return next;
            }, []);

        return {
            ...workspaceMembership,
            ...projectMembership,
            member: projectMembership?.member || workspaceMembership?.member,
            user_roles: roles,
        };
    });
}

export function attachProjectRolesToMemberships(memberships, projectRoles) {
    const roles = roleItems(projectRoles);
    return (Array.isArray(memberships) ? memberships : []).map((membership) => ({
        ...membership,
        user_roles: [
            ...roles.filter((role) => sameWorkspaceMembership(role?.workspace_membership, membership)),
            ...roleItems(membership?.user_roles),
        ],
    }));
}

function projectScopeKeys(project) {
    return new Set(entityKeys(project));
}

export function findProjectMemberRole(workspaceMembership, project) {
    const scopeKeys = projectScopeKeys(project);
    const roles = roleItems(workspaceMembership?.user_roles);

    return roles.find((role) => (
        role?.scope_type === "project"
        && scopeKeys.has(String(role?.scope_id))
    )) || null;
}

export function createProjectMemberEntry(workspaceMembership, project) {
    const userRole = findProjectMemberRole(workspaceMembership, project);
    return {
        workspaceMembership,
        userRole,
        role: userRole?.role || "viewer",
    };
}

export function createProjectMemberEntries(workspaceMemberships, project) {
    return (Array.isArray(workspaceMemberships) ? workspaceMemberships : [])
        .filter(Boolean)
        .map((membership) => createProjectMemberEntry(membership, project));
}

function normalizePermissions(value) {
    let permissions = value;
    if (typeof permissions === "string") {
        try {
            permissions = JSON.parse(permissions);
        } catch {
            permissions = [permissions];
        }
    }
    if (permissions && !Array.isArray(permissions) && typeof permissions === "object") {
        permissions = Object.entries(permissions)
            .filter(([, enabled]) => enabled === true)
            .map(([permission]) => permission);
    }
    return [...new Set((Array.isArray(permissions) ? permissions : [])
        .map((permission) => String(permission || "").trim())
        .filter(Boolean))]
        .sort();
}

function samePermissions(left, right) {
    return JSON.stringify(normalizePermissions(left)) === JSON.stringify(normalizePermissions(right));
}

export function buildProjectMemberRoleMutations({
    previousMemberships,
    entries,
    project,
    rolesConfig,
}) {
    const selectedEntries = Array.isArray(entries) ? entries : [];
    const selectedMemberships = selectedEntries.map((entry) => entry.workspaceMembership);
    const canonicalScopeId = String(project?.documentId || project?.id || "");

    const upserts = selectedEntries.flatMap((entry) => {
        const membership = entry.workspaceMembership;
        if (!membership) return [];
        const existingRole = entry.userRole || findProjectMemberRole(membership, project);
        const role = entry.role || "viewer";
        const permissions = normalizePermissions(rolesConfig?.[role]);
        const needsWrite = !existingRole
            || existingRole.role !== role
            || String(existingRole.scope_id || "") !== canonicalScopeId
            || !samePermissions(existingRole.permissions, permissions);

        return needsWrite ? [{membership, existingRole, role, permissions}] : [];
    });

    const removals = (Array.isArray(previousMemberships) ? previousMemberships : [])
        .filter((membership) => !selectedMemberships.some((selected) => (
            sameWorkspaceMembership(selected, membership)
        )))
        .map((membership) => findProjectMemberRole(membership, project))
        .filter(Boolean);

    return {upserts, removals};
}
